import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { designTokens } from '@/utils/design-tokens'
import type { DepartmentResponse } from '@/api/generated/models/DepartmentResponse'

interface DepartmentEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (departmentData: { name: string; sortKey?: number; available: boolean }) => void
  department: DepartmentResponse | null
  isLoading?: boolean
}

export default function DepartmentEditModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  department,
  isLoading = false 
}: DepartmentEditModalProps) {
  const [departmentName, setDepartmentName] = useState('')
  const [sortKey, setSortKey] = useState<string>('')
  const [available, setAvailable] = useState('true')

  // 모달이 열릴 때 기존 부서 정보로 폼 미리 채우기
  useEffect(() => {
    if (department && isOpen) {
      setDepartmentName(department.name || '')
      setSortKey(department.sort_key?.toString() || '')
      setAvailable((department.available ?? true).toString())
    }
  }, [department, isOpen])

  const handleSubmit = () => {
    if (departmentName.trim()) {
      const sortKeyNumber = sortKey.trim() ? parseInt(sortKey, 10) : undefined
      onSubmit({
        name: departmentName.trim(),
        sortKey: !isNaN(sortKeyNumber as number) ? sortKeyNumber : undefined,
        available: available === 'true'
      })
    }
  }

  const handleClose = () => {
    setDepartmentName('')
    setSortKey('')
    setAvailable('true')
    onClose()
  }

  // 폼이 변경되었는지 확인 (저장 버튼 활성화용)
  const hasChanges = department && (
    departmentName.trim() !== department.name || 
    sortKey !== (department.sort_key?.toString() || '') ||
    available !== (department.available ?? true).toString()
  )

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Background Blur Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />
      
      {/* Modal Content */}
      <div 
        className="relative bg-white rounded w-[600px] max-w-[90vw] max-h-[90vh] overflow-auto border"
        style={{ 
          borderColor: designTokens.colors.gray[1]
        }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2 
              className="font-bold"
              style={{ 
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6]
              }}
            >
              부서 수정
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ color: designTokens.colors.gray[5] }}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-6">
            {/* Form Section */}
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-[10px]">
                {/* Department Name Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label 
                        style={{ 
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        부서명
                        <span 
                          className="ml-1"
                          style={{ 
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                            lineHeight: designTokens.typography.fontSize['body-xsmall'][1].lineHeight,
                            color: designTokens.colors.error.DEFAULT
                          }}
                        >
                          *
                        </span>
                      </label>
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="부서명을 입력해주세요."
                        value={departmentName}
                        onChange={(e) => setDepartmentName(e.target.value)}
                        size="small"
                        className="w-full"
                        autoFocus
                      />
                    </div>
                  </div>
                </div>

                {/* Sort Key Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label 
                        style={{ 
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        순서
                      </label>
                    </div>
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="순서를 입력해주세요. (선택사항)"
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value)}
                        size="small"
                        className="w-full"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                {/* Available Status Select */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label 
                        style={{ 
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        상태
                      </label>
                    </div>
                    <div className="flex-1">
                      <select
                        value={available}
                        onChange={(e) => setAvailable(e.target.value)}
                        className="w-full h-10 px-3 border rounded"
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          color: designTokens.colors.gray[6],
                          borderColor: designTokens.colors.gray[1],
                          backgroundColor: designTokens.colors.white
                        }}
                      >
                        <option value="true">활성</option>
                        <option value="false">비활성</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-[10px]">
              <Button
                color="light"
                size="medium"
                onClick={handleClose}
                disabled={isLoading}
                className="px-[10px] h-10"
              >
                취소
              </Button>
              <Button
                color="dark" 
                size="medium"
                onClick={handleSubmit}
                disabled={isLoading || !departmentName.trim() || !hasChanges}
                className="px-[10px] h-10"
              >
                {isLoading ? '수정 중...' : '수정'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}